/**
 * Producto principal — variant picker + add to cart (Horizon custom section).
 * Idempotent: safe after Theme Editor section reloads / morph hydration.
 */
(function () {
  const INITIALIZED = 'data-producto-principal-inicializado';

  function formatearPrecio(centimos, ejemplo) {
    const numero = Number(centimos) / 100;
    const muestra = ejemplo || '';
    const simbolo = muestra.replace(/[\d\s.,]/g, '').trim() || '€';
    const conDecimales = /\.\d{2}|,\d{2}/.test(muestra);
    const valor = conDecimales ? numero.toFixed(2).replace('.', ',') : Math.round(numero).toString();
    if (muestra.indexOf(simbolo) === 0) return `${simbolo}${valor}`;
    return `${valor} ${simbolo}`;
  }

  function parseJson(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || '');
    } catch {
      return fallback;
    }
  }

  function isDefaultOnlyVariant(variantes) {
    return (
      variantes.length === 1 &&
      (variantes[0].title === 'Default Title' || variantes[0].option1 === 'Default Title')
    );
  }

  /** Shopify variant JSON uses featured_image; Liquid featured_media is exposed via data-producto-principal-variant-media. */
  function buildMediaIdLookup(seccion) {
    const filas = parseJson(seccion.querySelector('[data-producto-principal-variant-media]'), []);
    const porId = {};
    filas.forEach((fila) => {
      if (fila?.id != null && fila.media_id != null) {
        porId[String(fila.id)] = Number(fila.media_id);
      }
    });
    return { filas, porId };
  }

  function buildColorMediaLookup(seccion) {
    return parseJson(seccion.querySelector('[data-producto-principal-color-media]'), []);
  }

  function mediaIdPorColor(variante, colorMedia) {
    if (!variante || !colorMedia.length) return null;
    const filaMapa = colorMedia.find((fila) => {
      if (!fila?.color || fila.media_id == null) return false;
      const pos = fila.posicion_opcion || 1;
      return String(variante[`option${pos}`] ?? '') === String(fila.color);
    });
    if (filaMapa?.media_id) return Number(filaMapa.media_id);
    if (variante.option1) {
      const porNombre = colorMedia.find(
        (fila) => fila.media_id != null && String(fila.color) === String(variante.option1)
      );
      if (porNombre?.media_id) return Number(porNombre.media_id);
    }
    return null;
  }

  function mediaIdDeVariante(variante, { porId, filas, variantes, colorMedia }) {
    if (!variante) return null;

    const mapped = porId[String(variante.id)];
    if (mapped) return mapped;

    if (variante.featured_media?.id) return Number(variante.featured_media.id);
    if (variante.featured_image?.id) return Number(variante.featured_image.id);

    const porColor = mediaIdPorColor(variante, colorMedia || []);
    if (porColor) return porColor;

    const opcionesVariante = Array.isArray(variante.options) ? variante.options : [];
    if (opcionesVariante.length && filas.length) {
      const hermana = filas.find((fila) => {
        if (fila.id === variante.id || fila.media_id == null) return false;
        if (!Array.isArray(fila.options) || fila.options.length !== opcionesVariante.length) return false;
        return fila.options[0] === opcionesVariante[0];
      });
      if (hermana?.media_id) return Number(hermana.media_id);
    }

    if (variantes?.length && variante.option1) {
      const otra = variantes.find(
        (v) =>
          v.id !== variante.id &&
          v.option1 === variante.option1 &&
          (porId[String(v.id)] || v.featured_image?.id || v.featured_media?.id)
      );
      if (otra) {
        return (
          porId[String(otra.id)] ||
          Number(otra.featured_media?.id || otra.featured_image?.id) ||
          null
        );
      }
    }

    return null;
  }

  function encontrarVariante(variantes, seleccion) {
    return variantes.find((variante) =>
      Object.entries(seleccion).every(([posicion, valor]) => {
        if (!valor) return true;
        const clave = `option${posicion}`;
        return String(variante[clave] ?? '') === String(valor);
      })
    );
  }

  /** Primera variante que coincide con las opciones ya elegidas (p. ej. solo color). */
  function varianteParaImagen(variantes, seleccion) {
    const posiciones = Object.keys(seleccion).filter((pos) => seleccion[pos]);
    if (!posiciones.length) return null;
    const coincidencias = variantes.filter((variante) =>
      posiciones.every((pos) => String(variante[`option${pos}`] ?? '') === String(seleccion[pos]))
    );
    if (!coincidencias.length) return null;
    return coincidencias.find((v) => v.available) || coincidencias[0];
  }

  function archivoImagenVariante(variante) {
    const src =
      variante?.featured_image?.src ||
      variante?.featured_media?.preview_image?.src ||
      '';
    if (!src) return '';
    return src.split('?')[0].split('/').pop();
  }

  function seleccionCompleta(seleccion, opciones) {
    if (!opciones.length) return true;
    return opciones.every((_, indice) => Boolean(seleccion[String(indice + 1)]));
  }

  function tituloVarianteCompacto(variante) {
    if (!variante) return '';
    if (variante.title && variante.title !== 'Default Title') return variante.title;
    return [variante.option1, variante.option2, variante.option3].filter(Boolean).join(' · ');
  }

  function actualizarStickyResumen(seccion, variante) {
    const nodo = seccion.querySelector('[data-producto-principal-sticky-variante]');
    if (!nodo) return;
    const texto = tituloVarianteCompacto(variante);
    if (!texto) {
      nodo.hidden = true;
      nodo.textContent = '';
      return;
    }
    nodo.hidden = false;
    nodo.textContent = texto;
  }

  function initLightbox(seccion) {
    const dialogo = seccion.querySelector('[data-producto-principal-lightbox]');
    const imagen = seccion.querySelector('[data-producto-principal-lightbox-imagen]');
    const cerrar = seccion.querySelector('[data-producto-principal-lightbox-cerrar]');
    if (!dialogo || !imagen) return;

    seccion.querySelectorAll('[data-producto-principal-zoom]').forEach((boton) => {
      boton.addEventListener('click', () => {
        const src = boton.dataset.zoomSrc;
        if (!src) return;
        imagen.src = src;
        imagen.alt = boton.querySelector('img')?.alt || '';
        if (dialogo.showModal) dialogo.showModal();
      });
    });

    cerrar?.addEventListener('click', () => dialogo.close());
    dialogo.addEventListener('click', (evento) => {
      if (evento.target === dialogo) dialogo.close();
    });
  }

  function initCompletaLook(seccion) {
    const modulo = seccion.querySelector('[data-pdp-completa-look]');
    if (!modulo) return;
    const urlAnadir = modulo.dataset.urlAnadir || seccion.dataset.urlAnadir;
    const urlCarrito = modulo.dataset.urlCarrito || seccion.dataset.urlCarrito;
    const feedback = modulo.querySelector('[data-pdp-look-feedback]');

    modulo.querySelectorAll('[data-pdp-look-variante]').forEach((select) => {
      if (select.tagName !== 'SELECT') return;
      select.addEventListener('change', () => {
        const item = select.closest('[data-pdp-look-item]');
        const precio = select.selectedOptions[0]?.dataset.precio;
        const precioEl = item?.querySelector('[data-pdp-look-precio] span');
        if (precio && precioEl) precioEl.textContent = precio;
      });
    });

    modulo.addEventListener('click', async (evento) => {
      const boton = evento.target.closest('[data-pdp-look-anadir]');
      if (!boton) return;
      evento.preventDefault();
      const item = boton.closest('[data-pdp-look-item]');
      const selector = item?.querySelector('[data-pdp-look-variante]');
      const varianteId = selector?.value;
      if (!varianteId || !urlAnadir) return;
      boton.disabled = true;
      if (feedback) {
        feedback.hidden = false;
        feedback.textContent = seccion.dataset.textoAnadiendo || 'Añadiendo…';
      }
      try {
        const respuesta = await fetch(urlAnadir, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(varianteId), quantity: 1 }] }),
        });
        const datos = await respuesta.json();
        if (datos.status) throw new Error(datos.description || 'Error');
        const carrito = await fetch(urlCarrito, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: carrito, abrirDrawer: true, mostrarToast: true },
          })
        );
        if (feedback) feedback.hidden = true;
      } catch (error) {
        if (feedback) feedback.textContent = seccion.dataset.textoError || 'No se pudo añadir';
      } finally {
        boton.disabled = false;
      }
    });
  }

  function marcarErrorFeedback(feedback, mensaje) {
    if (!feedback || !mensaje) return;
    feedback.hidden = false;
    feedback.classList.add('producto-principal__feedback--error');
    feedback.textContent = mensaje;
  }

  function limpiarErrorFeedback(feedback) {
    if (!feedback) return;
    feedback.classList.remove('producto-principal__feedback--error');
    feedback.hidden = true;
    feedback.textContent = '';
  }

  async function verificarLineaEnCarrito(urlCarrito, varianteId) {
    try {
      const carrito = await fetch(urlCarrito, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const linea = carrito?.items?.find((i) => String(i.variant_id) === String(varianteId));
      return { ok: Boolean(linea), carrito, linea };
    } catch {
      return { ok: false, carrito: null, linea: null };
    }
  }

  function avisarSeleccionIncompleta(seccion, feedback, mensaje) {
    const contenedor = seccion.querySelector('[data-producto-principal-opciones]');
    if (contenedor) {
      contenedor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      contenedor.classList.add('producto-principal__opciones--atencion');
      window.setTimeout(() => contenedor.classList.remove('producto-principal__opciones--atencion'), 2400);
    }
    if (feedback) {
      feedback.hidden = false;
      feedback.classList.add('producto-principal__feedback--error');
      feedback.textContent = mensaje;
    }
  }

  function varianteDesdeUrl(variantes) {
    const params = new URLSearchParams(window.location.search);
    const varianteId = Number(params.get('variant'));
    if (!varianteId) return null;
    return variantes.find((v) => Number(v.id) === varianteId) || null;
  }

  function seleccionDesdePills(seccion, opciones) {
    const seleccion = {};
    opciones.forEach((_, indice) => {
      const posicion = String(indice + 1);
      const pillActiva = seccion.querySelector(
        `[data-producto-principal-pill][data-opcion="${posicion}"].producto-principal__pill--activa`
      );
      seleccion[posicion] = pillActiva?.dataset.valor || null;
    });
    return seleccion;
  }

  function syncPillsToVariant(seccion, variante, opciones) {
    if (!variante) return;
    opciones.forEach((_, indice) => {
      const posicion = String(indice + 1);
      const valor = variante[`option${posicion}`];
      if (!valor || valor === 'Default Title') return;
      seccion.querySelectorAll(`[data-producto-principal-pill][data-opcion="${posicion}"]`).forEach((pill) => {
        const activa = pill.dataset.valor === valor;
        pill.classList.toggle('producto-principal__pill--activa', activa);
        pill.setAttribute('aria-pressed', activa ? 'true' : 'false');
      });
      const etiquetaValor = seccion.querySelector(`[data-producto-principal-valor-opcion="${posicion}"]`);
      if (etiquetaValor) etiquetaValor.textContent = valor;
    });
  }

  function refreshPillAvailability(seccion, variantes, seleccion, opciones) {
    seccion.querySelectorAll('[data-producto-principal-pill]').forEach((pill) => {
      const posicion = pill.dataset.opcion;
      const valor = pill.dataset.valor;
      const prueba = { ...seleccion, [posicion]: valor };
      const match = encontrarVariante(variantes, prueba);
      const disponible = Boolean(match?.available);
      pill.classList.toggle('producto-principal__pill--agotada', match ? !disponible : true);
      pill.setAttribute('aria-disabled', match && disponible ? 'false' : 'true');
    });
  }

  function ocultarEtiquetasVarianteDefault() {
    document.querySelectorAll('[data-sticky-promo-variante], [data-aviso-stock-variante]').forEach((el) => {
      const texto = (el.textContent || '').trim();
      const esDefault =
        el.dataset.varianteTitulo === 'Default Title' ||
        texto === 'Default Title' ||
        texto === 'Variante: Default Title';
      const contenedor = el.closest('p') || el;
      if (esDefault) contenedor.hidden = true;
    });
  }

  function initProductoPrincipal(seccion) {
    if (!seccion || seccion.getAttribute(INITIALIZED) === 'true') return;

    const variantes = parseJson(seccion.querySelector('[data-producto-principal-variantes]'), []);
    const opciones = parseJson(seccion.querySelector('[data-producto-principal-opciones]'), []);
    const mediaLookup = buildMediaIdLookup(seccion);
    const colorMedia = buildColorMediaLookup(seccion);
    const formulario = seccion.querySelector('[data-producto-principal-formulario]');
    const inputVariante = seccion.querySelector('[data-producto-principal-variante-id]');
    const botonAnadir = seccion.querySelector('[data-producto-principal-boton-anadir]');
    const botonDeseos = seccion.querySelector('[data-lista-deseos-toggle]');
    const botonComparar = seccion.querySelector('[data-comparador-toggle]');
    const feedback = seccion.querySelector('[data-producto-principal-feedback]');
    const precioActual = seccion.querySelector('[data-producto-principal-precio-actual]');
    const precioTachado = seccion.querySelector('[data-producto-principal-precio-tachado]');
    const sticky = seccion.querySelector('[data-producto-principal-sticky]');
    const stickyPrecio = seccion.querySelector('[data-producto-principal-sticky-precio]');
    const stickyBoton = seccion.querySelector('[data-producto-principal-sticky-boton]');
    const visor = seccion.querySelector('[data-producto-principal-visor]');
    const diapositivas = Array.from(seccion.querySelectorAll('[data-producto-principal-diapositiva]'));
    const miniaturas = Array.from(seccion.querySelectorAll('[data-producto-principal-miniatura]'));
    const contadorActual = seccion.querySelector('[data-producto-principal-contador-actual]');
    const urlAnadir = seccion.dataset.urlAnadir;
    const textoAnadiendo = seccion.dataset.textoAnadiendo || botonAnadir?.textContent?.trim() || '';
    const textoError = seccion.dataset.textoError || 'Error';
    const textoAgotado = seccion.dataset.textoAgotado || '';
    const textoSeleccionIncompleta =
      seccion.dataset.textoSeleccionIncompleta || 'Elige todas las opciones antes de añadir al carrito.';
    const textoCombinacionInvalida =
      seccion.dataset.textoCombinacionInvalida ||
      'Esta combinación de talla y color no está disponible. Elige otras opciones.';
    const textoVarianteUrlInvalida =
      seccion.dataset.textoVarianteUrlInvalida ||
      'El enlace usa una variante que ya no existe. Elige talla y color de nuevo.';
    const textoErrorRed = seccion.dataset.textoErrorRed || textoError;
    let indiceActivo = Number(seccion.dataset.indiceMediaInicial || 0);
    let seleccion = seleccionDesdePills(seccion, opciones);
    let peticionCarrito = 0;

    const irADiapositiva = (indice) => {
      if (!diapositivas.length) return;
      indiceActivo = (indice + diapositivas.length) % diapositivas.length;
      diapositivas.forEach((diapositiva, i) => {
        const activa = i === indiceActivo;
        diapositiva.hidden = !activa;
        diapositiva.classList.toggle('producto-principal__diapositiva--activa', activa);
      });
      miniaturas.forEach((miniatura, i) => {
        const activa = i === indiceActivo;
        miniatura.classList.toggle('producto-principal__miniatura--activa', activa);
        miniatura.setAttribute('aria-selected', activa ? 'true' : 'false');
        miniatura.tabIndex = activa ? 0 : -1;
      });
      if (contadorActual) contadorActual.textContent = String(indiceActivo + 1);
    };

    const irAMediaVariante = (variante) => {
      if (!variante) return;
      const mediaId = mediaIdDeVariante(variante, { ...mediaLookup, variantes, colorMedia });
      if (mediaId) {
        const indice = diapositivas.findIndex((d) => Number(d.dataset.mediaId) === mediaId);
        if (indice >= 0) {
          irADiapositiva(indice);
          return;
        }
      }
      const archivo = archivoImagenVariante(variante);
      if (!archivo) return;
      const indiceSrc = diapositivas.findIndex((d) => {
        const img = d.querySelector('img');
        return img?.src && img.src.includes(archivo);
      });
      if (indiceSrc >= 0) irADiapositiva(indiceSrc);
    };

    const actualizarVariante = (variante) => {
      if (!variante) return;
      if (inputVariante) inputVariante.value = variante.id;

      const ejemplo = precioActual?.textContent?.trim();
      if (precioActual) precioActual.textContent = formatearPrecio(variante.price, ejemplo);
      if (stickyPrecio) stickyPrecio.textContent = formatearPrecio(variante.price, ejemplo);

      if (window.NaiaPrecio?.aplicarPrecioConCompare) {
        window.NaiaPrecio.aplicarPrecioConCompare(
          seccion.querySelector('[data-producto-principal-precio]'),
          variante.price,
          variante.compare_at_price
        );
      } else if (precioTachado) {
        if (variante.compare_at_price && variante.compare_at_price > variante.price) {
          precioTachado.hidden = false;
          precioTachado.textContent = formatearPrecio(variante.compare_at_price, ejemplo);
        } else {
          precioTachado.hidden = true;
          precioTachado.textContent = '';
        }
      }

      const disponible = variante.available;
      if (botonAnadir) {
        botonAnadir.disabled = !disponible;
        botonAnadir.textContent = disponible
          ? botonAnadir.dataset.textoDisponible || botonAnadir.textContent
          : textoAgotado || botonAnadir.dataset.textoAgotado || botonAnadir.textContent;
      }
      if (stickyBoton) {
        stickyBoton.disabled = !disponible;
        stickyBoton.textContent = disponible
          ? stickyBoton.dataset.textoDisponible || stickyBoton.textContent
          : textoAgotado || stickyBoton.textContent;
      }

      if (botonDeseos) {
        botonDeseos.dataset.varianteId = String(variante.id);
        botonDeseos.dataset.productoPrecio = String(variante.price);
        botonDeseos.dataset.productoPrecioFormateado = formatearPrecio(
          variante.price,
          precioActual?.textContent?.trim()
        );
        botonDeseos.dataset.productoDisponible = disponible ? 'true' : 'false';
      }

      if (botonComparar) {
        botonComparar.dataset.varianteId = String(variante.id);
        botonComparar.dataset.productoPrecio = String(variante.price);
        botonComparar.dataset.productoPrecioFormateado = formatearPrecio(
          variante.price,
          precioActual?.textContent?.trim()
        );
        botonComparar.dataset.productoDisponible = disponible ? 'true' : 'false';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variante.id);
      window.history.replaceState({}, '', url.toString());

      irAMediaVariante(variante);
      actualizarStickyResumen(seccion, variante);

      document.dispatchEvent(
        new CustomEvent('producto:variante-cambiada', {
          detail: { variante },
        })
      );
    };

    if (botonAnadir) {
      botonAnadir.dataset.textoDisponible = botonAnadir.textContent.trim();
      botonAnadir.dataset.textoAgotado = textoAgotado;
    }
    if (stickyBoton) {
      stickyBoton.dataset.textoDisponible = stickyBoton.textContent.trim();
    }

    seccion.addEventListener('click', (evento) => {
      const pill = evento.target.closest('[data-producto-principal-pill]');
      if (pill && seccion.contains(pill)) {
        if (pill.classList.contains('producto-principal__pill--agotada')) return;
        const posicion = pill.dataset.opcion;
        seleccion[posicion] = pill.dataset.valor;
        seccion.querySelectorAll(`[data-producto-principal-pill][data-opcion="${posicion}"]`).forEach((elemento) => {
          const activa = elemento === pill;
          elemento.classList.toggle('producto-principal__pill--activa', activa);
          elemento.setAttribute('aria-pressed', activa ? 'true' : 'false');
        });
        const etiquetaValor = seccion.querySelector(`[data-producto-principal-valor-opcion="${posicion}"]`);
        if (etiquetaValor) etiquetaValor.textContent = pill.dataset.valor;
        refreshPillAvailability(seccion, variantes, seleccion, opciones);
        const vistaImagen = varianteParaImagen(variantes, seleccion);
        if (vistaImagen) irAMediaVariante(vistaImagen);
        if (!seleccionCompleta(seleccion, opciones)) {
          limpiarErrorFeedback(feedback);
          if (botonAnadir) botonAnadir.disabled = true;
          if (stickyBoton) stickyBoton.disabled = true;
          return;
        }
        const varianteElegida = encontrarVariante(variantes, seleccion);
        if (!varianteElegida) {
          if (inputVariante) inputVariante.value = '';
          marcarErrorFeedback(feedback, textoCombinacionInvalida);
          if (botonAnadir) botonAnadir.disabled = true;
          if (stickyBoton) stickyBoton.disabled = true;
          return;
        }
        limpiarErrorFeedback(feedback);
        actualizarVariante(varianteElegida);
        return;
      }

      const miniatura = evento.target.closest('[data-producto-principal-miniatura]');
      if (miniatura && seccion.contains(miniatura)) {
        irADiapositiva(Number(miniatura.dataset.indice));
        return;
      }

      const anterior = evento.target.closest('[data-producto-principal-anterior]');
      if (anterior && seccion.contains(anterior)) {
        irADiapositiva(indiceActivo - 1);
        return;
      }

      const siguiente = evento.target.closest('[data-producto-principal-siguiente]');
      if (siguiente && seccion.contains(siguiente)) {
        irADiapositiva(indiceActivo + 1);
        return;
      }

      const pestana = evento.target.closest('[data-producto-principal-pestana]');
      if (pestana && seccion.contains(pestana)) {
        const indice = Number(pestana.dataset.indice);
        seccion.querySelectorAll('[data-producto-principal-pestana]').forEach((elemento, i) => {
          const activa = i === indice;
          elemento.classList.toggle('producto-principal__pestana--activa', activa);
          elemento.setAttribute('aria-selected', activa ? 'true' : 'false');
          elemento.tabIndex = activa ? 0 : -1;
        });
        seccion.querySelectorAll('[data-producto-principal-panel]').forEach((panel, i) => {
          panel.hidden = i !== indice;
        });
        return;
      }

      const menos = evento.target.closest('[data-producto-principal-cantidad-menos]');
      if (menos && seccion.contains(menos)) {
        const input = seccion.querySelector('[data-producto-principal-cantidad]');
        if (input) input.value = String(Math.max(1, Number(input.value || 1) - 1));
        return;
      }

      const mas = evento.target.closest('[data-producto-principal-cantidad-mas]');
      if (mas && seccion.contains(mas)) {
        const input = seccion.querySelector('[data-producto-principal-cantidad]');
        if (input) input.value = String(Number(input.value || 1) + 1);
        return;
      }

      if (evento.target.closest('[data-producto-principal-sticky-boton]') && seccion.contains(evento.target)) {
        evento.preventDefault();
        const varianteElegida = encontrarVariante(variantes, seleccion);
        if (!seleccionCompleta(seleccion, opciones) || !varianteElegida) {
          avisarSeleccionIncompleta(seccion, feedback, textoSeleccionIncompleta);
          return;
        }
        if (!varianteElegida.available) return;
        botonAnadir?.click();
      }
    });

    if (visor && diapositivas.length > 1) {
      let inicioX = 0;
      visor.addEventListener(
        'touchstart',
        (evento) => {
          inicioX = evento.changedTouches[0].screenX;
        },
        { passive: true }
      );

      visor.addEventListener(
        'touchend',
        (evento) => {
          const diferencia = evento.changedTouches[0].screenX - inicioX;
          if (Math.abs(diferencia) < 40) return;
          if (diferencia < 0) irADiapositiva(indiceActivo + 1);
          else irADiapositiva(indiceActivo - 1);
        },
        { passive: true }
      );

      visor.addEventListener('keydown', (evento) => {
        if (evento.key === 'ArrowLeft') {
          evento.preventDefault();
          irADiapositiva(indiceActivo - 1);
        }
        if (evento.key === 'ArrowRight') {
          evento.preventDefault();
          irADiapositiva(indiceActivo + 1);
        }
      });
    }

    const urlCarrito = seccion.dataset.urlCarrito || '/cart.js';

    const enviarCarrito = async (evento) => {
      if (!formulario) return;
      evento.preventDefault();
      if (seccion.dataset.anadiendo === 'true') return;

      const varianteElegida = encontrarVariante(variantes, seleccion);
      if (!seleccionCompleta(seleccion, opciones) || !varianteElegida) {
        avisarSeleccionIncompleta(seccion, feedback, textoSeleccionIncompleta);
        return;
      }
      if (!varianteElegida.available) return;

      const varianteId = inputVariante?.value;
      const cantidadInput = seccion.querySelector('[data-producto-principal-cantidad]');
      const cantidad = Number(cantidadInput?.value || 1);

      peticionCarrito += 1;
      const idPeticion = peticionCarrito;
      seccion.dataset.anadiendo = 'true';

      if (feedback) {
        feedback.hidden = false;
        feedback.classList.remove('producto-principal__feedback--error');
        feedback.textContent = seccion.dataset.textoAnadiendo || textoAnadiendo;
      }

      if (botonAnadir) {
        botonAnadir.disabled = true;
        botonAnadir.textContent = seccion.dataset.textoAnadiendo || textoAnadiendo;
      }
      if (stickyBoton) stickyBoton.disabled = true;

      const controlador = new AbortController();
      const temporizador = window.setTimeout(() => controlador.abort(), 20000);

      const emitirExito = (carrito, datos) => {
        limpiarErrorFeedback(feedback);
        const item = datos?.items?.[0];
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: carrito,
              itemCount: carrito?.item_count ?? datos?.item_count,
              abrirDrawer: true,
              mostrarToast: true,
              producto: {
                titulo: item?.product_title || item?.title || seccion.dataset.productoTitulo || '',
                imagen: item?.image || seccion.dataset.productoImagen || '',
                url: item?.url || seccion.dataset.productoUrl || '',
              },
            },
          })
        );
      };

      try {
        const respuesta = await fetch(urlAnadir, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number(varianteId), quantity: cantidad }],
          }),
          signal: controlador.signal,
        });

        const datos = await respuesta.json();
        if (idPeticion !== peticionCarrito) return;

        if (datos.status) {
          marcarErrorFeedback(feedback, datos.description || datos.message || textoError);
        } else {
          const item = datos.items?.[0];
          if (item && String(item.variant_id) !== String(varianteId)) {
            marcarErrorFeedback(feedback, textoError);
            return;
          }
          let carrito = null;
          try {
            carrito = await fetch(urlCarrito, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          } catch {
            carrito = null;
          }
          emitirExito(carrito, datos);
        }
      } catch (error) {
        if (idPeticion !== peticionCarrito) return;
        const verificacion = await verificarLineaEnCarrito(urlCarrito, varianteId);
        if (verificacion.ok) {
          emitirExito(verificacion.carrito, { items: [verificacion.linea], item_count: verificacion.carrito?.item_count });
        } else {
          marcarErrorFeedback(feedback, textoErrorRed);
        }
      } finally {
        window.clearTimeout(temporizador);
        if (idPeticion !== peticionCarrito) return;
        delete seccion.dataset.anadiendo;
        const variante = encontrarVariante(variantes, seleccion);
        const disponible = Boolean(variante?.available);
        if (botonAnadir) {
          botonAnadir.disabled = !disponible;
          botonAnadir.textContent = disponible
            ? botonAnadir.dataset.textoDisponible || 'Añadir al carrito'
            : textoAgotado || botonAnadir.dataset.textoAgotado || '';
        }
        if (stickyBoton) {
          stickyBoton.disabled = !disponible;
          stickyBoton.textContent = disponible
            ? stickyBoton.dataset.textoDisponible || stickyBoton.textContent
            : textoAgotado || stickyBoton.textContent;
        }
      }
    };

    formulario?.addEventListener('submit', enviarCarrito);

    if (sticky && botonAnadir) {
      const observador = new IntersectionObserver(
        ([entrada]) => {
          const visible = !entrada.isIntersecting;
          sticky.hidden = !visible;
          document.body.classList.toggle('producto-principal-sticky-visible', visible);
        },
        { threshold: 0, rootMargin: '0px 0px -12px 0px' }
      );
      observador.observe(botonAnadir);
    }

    irADiapositiva(indiceActivo);

    const paramsUrl = new URLSearchParams(window.location.search);
    const rawVariantId = Number(paramsUrl.get('variant'));
    const varianteUrl = varianteDesdeUrl(variantes);
    let varianteInicial = null;

    if (rawVariantId && !varianteUrl) {
      marcarErrorFeedback(feedback, textoVarianteUrlInvalida);
      varianteInicial =
        encontrarVariante(variantes, seleccion) ||
        variantes.find((v) => v.available) ||
        variantes[0];
    } else if (varianteUrl) {
      varianteInicial = varianteUrl;
      if (!varianteUrl.available) {
        marcarErrorFeedback(feedback, textoAgotado || 'Agotado');
      }
    } else {
      varianteInicial =
        encontrarVariante(variantes, seleccion) ||
        variantes.find((v) => v.available) ||
        variantes[0];
    }

    if (varianteInicial) {
      syncPillsToVariant(seccion, varianteInicial, opciones);
      seleccion = seleccionDesdePills(seccion, opciones);
    }
    refreshPillAvailability(seccion, variantes, seleccion, opciones);
    if (varianteInicial) actualizarVariante(varianteInicial);

    initLightbox(seccion);
    initCompletaLook(seccion);

    if (isDefaultOnlyVariant(variantes)) {
      seccion.querySelector('[data-producto-principal-opciones]')?.remove();
    }

    seccion.setAttribute(INITIALIZED, 'true');
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-producto-principal]').forEach((seccion) => {
      initProductoPrincipal(seccion);
    });
    ocultarEtiquetasVarianteDefault();
  }

  function onSectionLoad(event) {
    const target = event.target;
    if (!target) return;
    if (target.matches?.('[data-producto-principal]')) {
      target.removeAttribute(INITIALIZED);
      initProductoPrincipal(target);
    } else {
      initAll(target);
    }
    ocultarEtiquetasVarianteDefault();
  }

  function onSectionUnload(event) {
    event.target?.querySelectorAll?.('[data-producto-principal]')?.forEach((seccion) => {
      seccion.removeAttribute(INITIALIZED);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:reorder', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);

  document.addEventListener('producto:variante-cambiada', (evento) => {
    const titulo = evento.detail?.variante?.title;
    if (titulo && titulo !== 'Default Title') return;
    ocultarEtiquetasVarianteDefault();
  });
})();
